import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { FilterBar, FilterField } from '@/components/composed/FilterBar';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  InvoiceTable,
  InvoiceDetailSheet,
  InvoiceEditDialog,
  invoiceListQuery,
} from '@/features/invoices';
import type { Invoice, InvoiceListParams } from '@/lib/api/domains/invoices';
import { canEdit } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { invoiceId } from '@/lib/api/ids';

/*
 * Invoice list route (spec §7.1, §7.6). Table state lives in the URL via
 * validateSearch (Zod); the loader primes the first page via ensureQueryData.
 * The detail sheet is deep-linkable through the `inspect` search param.
 */
const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(25),
  sort: z.string().optional(),
  status: z.enum(['draft', 'open', 'paid', 'void', 'overdue']).optional(),
  q: z.string().optional(),
  inspect: z.string().optional(),
});

export const Route = createFileRoute('/_auth/invoices/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const params = toParams(deps);
    return context.queryClient.ensureQueryData(invoiceListQuery(params));
  },
  component: InvoicesPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function toParams(search: z.infer<typeof searchSchema>): InvoiceListParams {
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.q ? { q: search.q } : {}),
  };
}

function InvoicesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useAuthStore((s) => s.session);
  const [createOpen, setCreateOpen] = useState(false);

  const params = toParams(search);

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  usePageMeta({ title: 'Invoices' });

  return (
    <Page bleed>
      <PageHeader
        title="Invoices"
        description="Server-paginated. State is in the URL — bookmark or share any view."
        actions={
          canEdit(session) ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              New invoice
            </Button>
          ) : null
        }
      />

      <Section>
        <FilterBar
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined, page: 1 })}
          searchLabel="Search"
          searchPlaceholder="Search by number or client…"
          onClear={
            search.q || search.status
              ? () => setSearch({ q: undefined, status: undefined, page: 1 })
              : undefined
          }
        >
          <FilterField label="Status">
            <Select
              value={search.status ?? 'all'}
              onValueChange={(value) =>
                setSearch({
                  status:
                    value === 'all'
                      ? undefined
                      : (value as z.infer<typeof searchSchema>['status']),
                  page: 1,
                })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </FilterBar>

        <InvoiceTable
          params={params}
          onPageChange={(page) => setSearch({ page })}
          onRowClick={(invoice: Invoice) => setSearch({ inspect: invoice.id })}
          onEdit={(invoice: Invoice) => setSearch({ inspect: invoice.id })}
        />
      </Section>

      <InvoiceDetailSheet
        invoiceId={search.inspect ? invoiceId(search.inspect) : null}
        open={Boolean(search.inspect)}
        onOpenChange={(open) =>
          setSearch({ inspect: open ? search.inspect : undefined })
        }
      />

      <InvoiceEditDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}
